import { useEffect, useRef, useState } from 'react';
import './ActionsMenu.css';

export interface ActionItem {
  label: string;
  onSelect: () => void;
  /** Red text — for actions that take something away. */
  danger?: boolean;
  disabled?: boolean;
  /** Hidden entirely (vs disabled, which shows it greyed out). */
  hidden?: boolean;
}

/**
 * "Actions ▾" button opening a list of operations for one table row. The list is
 * position: fixed under the button, so the table's overflow: hidden can't clip
 * it; it closes on a pick, an outside click, Escape, scroll or resize.
 */
const ActionsMenu = ({ items, label = 'Actions' }: { items: ActionItem[]; label?: string }) => {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const visible = items.filter((item) => !item.hidden);

  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos]);

  const toggle = () => {
    if (pos) return setPos(null);
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-secondary btn-sm actions-menu-button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={!!pos}
      >
        {label} <span aria-hidden="true">▾</span>
      </button>
      {pos && (
        <ul ref={menuRef} className="actions-menu" role="menu" style={{ top: pos.top, right: pos.right }}>
          {visible.map((item) => (
            <li key={item.label} role="none">
              <button
                type="button"
                role="menuitem"
                className={`actions-menu-item${item.danger ? ' danger' : ''}`}
                disabled={item.disabled}
                onClick={() => {
                  setPos(null);
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default ActionsMenu;
